import type {
  ImmersivePlugin,
  NavigationItem,
  PluginComponent,
  CustomModelPickerProps,
} from '@github-ui/copilot-chat/plugin'
import {AGENTS_PLUGIN_ID, AGENTS_PLUGIN_NAME, getAgentsPath} from './utils/constants'
import {AgentIcon} from '@primer/octicons-react'
import {copilotLocalStorage} from '@github-ui/copilot-chat-helpers/copilot-local-storage'
import {COPILOT_PATH} from '@github-ui/copilot-chat-helpers/copilot-chat-helpers'
import type {CopilotChatState} from '@github-ui/copilot-chat-state/copilot-chat-reducer'
import {ModeSupportName} from '@github-ui/copilot-chat-types'
import {sendEvent} from '@github-ui/hydro-analytics'
import type {useNavigate} from '@github-ui/use-navigate'
import {submitTask} from './utils/submit-task'
import type {RefObject} from 'react'
import type {HandleUserSubmit} from '@github-ui/one-copilot-composer/user-submit'
import {SlashCommandModelPicker} from './components/slash-command-model-picker/SlashCommandModelPicker'

export const COPILOT_AGENTS_PATH = `${COPILOT_PATH}/agents`

export class AgentsPlugin implements ImmersivePlugin {
  id = AGENTS_PLUGIN_ID

  displayName = AGENTS_PLUGIN_NAME

  constructor(
    createTaskFromInput: boolean = false,
    handleUserSubmitRef?: RefObject<HandleUserSubmit>,
    agentsEnabled: boolean = false,
    managedDefaultModelEnabled: boolean = false,
  ) {
    this.createTaskFromInput = createTaskFromInput
    this.handleUserSubmitRef = handleUserSubmitRef
    this.agentsEnabled = agentsEnabled
    this.managedDefaultModelEnabled = managedDefaultModelEnabled

    this.components = []
    this.navigationItems = [
      {
        id: AGENTS_PLUGIN_ID,
        displayName: 'Agents',
        href: getAgentsPath(),
        icon: AgentIcon,
        hardNavigation: true,
      },
    ]
  }

  createTaskFromInput: boolean

  handleUserSubmitRef: RefObject<HandleUserSubmit> | undefined

  agentsEnabled: boolean

  managedDefaultModelEnabled: boolean

  skipSyncRouteToThread = true

  components: PluginComponent[]

  slashCommand = {
    key: 'agent',
    name: 'Agent',
    description: 'Give Copilot cloud agent a background task',
    icon: AgentIcon,
    order: 1,
    enabled: () => this.agentsEnabled,
    disabledAction: (navigate: ReturnType<typeof useNavigate>) => {
      navigate(getAgentsPath(), {preventTurbo: true})
    },
    execute: async (params: string, chatState: CopilotChatState, navigate: ReturnType<typeof useNavigate>) => {
      // If this is an existing chat thread, we want to send a chat message instead of navigating to the agents page.
      if (this.agentsEnabled && chatState.selectedThreadID) {
        return false
      }

      // If agents are enabled and we can create tasks directly, do that
      if (this.agentsEnabled && this.createTaskFromInput && this.handleUserSubmitRef) {
        sendEvent('agent_sessions.task_view', {
          target: 'NEW_TASK_SUBMIT',
        })
        await submitTask(params, this.handleUserSubmitRef)
        return true
      }

      // Otherwise, always navigate to agents page (for disabled agents or when we can't create tasks directly)
      copilotLocalStorage.setTaskEntrypointMessage(params)
      navigate(getAgentsPath(), {preventTurbo: true})
      return true
    },
    suggestions: [
      'Explain this repository and help me understand the codebase',
      'Analyze test coverage and add missing tests',
      'Create a detailed implementation plan for a feature',
    ],
    mode: {
      name: ModeSupportName.Agent,
      icon: AgentIcon,
      placeholder: 'Give Copilot a background task to work on',
      supportsModelSwitching: true,
      supportsReferenceAttachments: true,
      supportedReferenceTypes: ['image', 'file', 'snippet'] as const,
      supportsAttachmentsMenu: false,
    },
    requiresModelSelectionReady: (chatState: CopilotChatState) =>
      this.managedDefaultModelEnabled &&
      this.agentsEnabled &&
      this.createTaskFromInput &&
      !!this.handleUserSubmitRef &&
      !chatState.selectedThreadID,
    customModelPicker: ({
      selectedAgent,
      announcementElementRef,
      onModelSelectionChange,
      isAhpEnvironment,
    }: CustomModelPickerProps) => (
      <SlashCommandModelPicker
        selectedAgent={selectedAgent}
        managedDefaultModelEnabled={this.managedDefaultModelEnabled}
        announcementElementRef={announcementElementRef}
        onModelSelectionChange={onModelSelectionChange}
        isAhpEnvironment={isAhpEnvironment}
      />
    ),
  }

  navigationItems: NavigationItem[]
}
